// Sugalarm — phone-sized header strip (cycle 36.1).
//
// Extracted from MobileFrame.tsx so the preview overlay can render a
// synthetic copy of the header above a non-urgent Now-screen preview
// without re-mounting the full MobileFrame (which owns the scroll
// area + tab bar). Pixel-identical to the inline header it replaces.
//
// Takes a narrow prop surface — in particular, `latest: Reading | null`
// instead of the full GlucoseSocketState, so the preview overlay can
// synthesize a one-reading source without stubbing the rest of the
// socket shape.

import {
  displayValue,
  fmtAge,
  trendInfo,
  zoneColor,
  zoneLabel,
  zoneTone,
} from "../../lib/glucose";
import type { DisplayGlucose } from "../../lib/currentGlucose";
import type { User, Zone } from "../../lib/types";
import { TrendGlyphSvg } from "../glucose/TrendGlyphSvg";
import { MemberAvatar } from "../ui/MemberAvatar";
import { Pill } from "../ui/Pill";
// Cycle 165: the shared mobile glass-bar treatment (frosted fill + sheen
// + no-backdrop-filter fallback) lives on MobileFrame's stylesheet — the
// header is part of that frame's chrome, imported cross-module like the
// cycle-30 LogScreen `.entry*` precedent.
import frameStyles from "./MobileFrame.module.css";
import { ProviderPill } from "./ProviderPill";

interface Props {
  user: User | null;
  /** ⚠ Cycle 435: the header's mini readout is a `DisplayGlucose` the
   *  selector (`lib/currentGlucose.ts`) released as CURRENT, or `null` —
   *  which collapses the readout to the cycle-33 "— No Data" treatment
   *  exactly as a stale reading always did. A raw `Reading` cannot be
   *  passed here; that is the enforcement. */
  current: DisplayGlucose | null;
  /** Cycle 306: the active member's CONNECTED provider (member.cgm_provider,
   *  member-scoped + fanned out on connect/disconnect). Preferred over the
   *  last reading's provider so a Libre → Dexcom switch reflects immediately
   *  and a disconnect shows "Sugalarm" (null). Optional — when omitted (the
   *  synthetic PreviewOverlay), the pill falls back to the reading provider,
   *  byte-identical to the cycle-109 behavior. */
  cgmProvider?: string | null;
  /** Cycle 57.5: `now` + `h24` retained on the prop surface so every
   *  caller (MobileFrame + PreviewOverlay) keeps compiling unchanged,
   *  but the body no longer reads them — the clock span they fed was
   *  removed (the OS status bar already shows the time on phones). */
  now: number;
  units: "mg/dL" | "mmol/L";
  ageMin: number;
  zone: Zone;
  memberName: string | null;
  /** Cycle 105: the active member's avatar id (null → Sugalarm shield). */
  memberAvatarId?: string | null;
  predictionPill?: string | null;
  h24?: boolean;
  /** Cycle 116: portrait Now-route large-title mode — the name becomes
   *  the page title (bold ~26px) and the cycle-109 mini reading +
   *  ProviderPill are suppressed (the hero below carries the value /
   *  trend / freshness, so the header copy is pure duplication). */
  nowTitle?: boolean;
  /** Cycle 116: the active member is the account's self-member — drives
   *  the muted "you" affix in large-title mode (only shown when >1
   *  member is accessible, which `nowTitle` already implies). */
  isSelfMember?: boolean;
}

export function MobileHeader({
  user,
  current,
  cgmProvider,
  units,
  ageMin,
  zone,
  memberName,
  memberAvatarId = null,
  predictionPill,
  nowTitle = false,
  isSelfMember = false,
}: Props) {
  // Cycle 435: "stale" here means exactly "the selector released no
  // current value" — the server's verdict, the sample age and the stream
  // confirmation are all folded into that one fact upstream.
  const stale = current == null;
  const heroColor = stale ? "var(--ink-3)" : zoneColor(zone);
  const displayName = memberName ?? user?.display_name ?? "Sugar Alarm";

  // Cycle 116: portrait Now-route large-title header. The name is the
  // page title (iOS large-title feel); no mini reading / ProviderPill —
  // the hero directly below owns the value, trend, and freshness.
  if (nowTitle) {
    // Cycle 227: stale OR no reading → an honest "No Data" pill (urgent
    // tone, matching the hero), otherwise the zone status pill.
    const noData = current == null;
    return (
      <div
        // Cycle 227 removed the cycle-165 glassChrome band here for a
        // seamless look ("no seam" against the status bar). Cycle 344
        // replaced it with a bespoke dark scrim (measured, legible) but the
        // operator explicitly asked for the real glass treatment back —
        // "I know I took it out but I think the design needs it." Cycle
        // 345 restores `.glassChrome`, the SAME class the standard
        // (non-nowTitle) header and the tab bar already use successfully
        // on this same MobileBackground — measured: --glass-fill is a 60%-
        // WEIGHTED opaque white mix, so even against a near-black photo
        // patch the composited surface floors at ~(157,157,157), a solid
        // mid-gray. `--ink` (dark navy) clears 5.4:1+ against that floor
        // in every measured case — safe to revert unchanged. `--ink-3`
        // (muted) and the Pill's thin zone-soft tint do NOT clear that
        // floor (measured ~1.8–2.8:1) — those two get the targeted fixes
        // below; the glass bar itself and the main name text are a clean
        // revert to pre-344.
        className={frameStyles.glassChrome}
        // Cycle 383: the phone's hidden dev-gated voice trigger (a ≥3 s
        // long-press). The HEADER BAR itself is the target — chosen because
        // it is the least collision-prone element in the mobile frame: it
        // contains NO interactive descendants in either of its two forms
        // (avatar, name, status pill and prediction pill are all inert), it
        // is present on every screen, and it is a large, stable hit area.
        // Marking the bar rather than a child also means zero layout risk —
        // this is an attribute, not a wrapper element.
        //
        // The attribute is INERT on its own: the only reader is a
        // document-level listener that `useVoiceCommands` registers ONLY
        // when localStorage.sugalarm_voice_dev === "1". Flag unset (the
        // production condition) → no listener, no handler, no visual or
        // behavioural change. Scaffolding — the wake word replaces it.
        data-voice-trigger="mobile-header"
        style={{
          height: 60,
          padding: "0 14px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
        }}
      >
        {/* Cycle 341: 38 → 44 (design audit — reads clearly without
            crowding the large-title row; header height unchanged). */}
        <MemberAvatar avatarId={memberAvatarId} name={displayName} size={44} />
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "baseline",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 26,
              fontWeight: 800,
              // Cycle 345: reverted to --ink (pre-344) — measured safe
              // against .glassChrome over any photo (5.4:1+ worst case).
              color: "var(--ink)",
              letterSpacing: "-0.02em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName}
          </span>
          {isSelfMember && (
            <span
              style={{
                fontSize: "var(--type-floor)",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                // Cycle 345: was --ink-3 (pre-344) — measured only ~1.8:1
                // against .glassChrome's worst-case composite, an AA fail
                // --ink-3 always had, just never over a photo before.
                // --ink itself is the proven-safe floor; the "you" affix
                // keeps its distinct hierarchy via size/case/spacing
                // instead of a lighter color.
                color: "var(--ink)",
                flexShrink: 0,
              }}
            >
              you
            </span>
          )}
        </div>
        {/* Cycle 227: the In range / Low / High status pill, right-aligned
            on the identity row (the name's flex:1 pushes it right). Keeps
            the existing zone color logic; never shrinks below the pill's
            own content.
            Cycle 345 gave it a solid --bg-elev backing because the Pill's
            thin zone tint let the photo behind the glass decide contrast.
            Cycle 451.2 replaces that with the shared status-pill look
            (appearance="status": white ink on an opaque state-tinted
            backing), so it matches every other status pill. The dot stays
            on the zone's raw color. */}
        <Pill
          tone={noData ? "urgent" : zoneTone(zone)}
          appearance="status"
          style={{ flexShrink: 0 }}
        >
          <span
            style={{
              display: "inline-block",
              width: 6,
              height: 6,
              borderRadius: 999,
              background: noData ? "var(--zone-urgent)" : zoneColor(zone),
            }}
          />
          {noData ? "No Data" : zoneLabel(zone)}
        </Pill>
        {predictionPill && (
          <div
            // Cycle 345: same fix as the status Pill above — --accent-soft
            // is a thin tint (measured failing through the glass in
            // darker regions); a solid opaque --bg-elev background removes
            // the dependency on what's underneath. --accent itself (kept)
            // was never the problem — it already clears AA on any solid
            // light surface, per the same reasoning as --zone-*-text.
            style={{
              fontSize: "var(--type-floor)",
              fontWeight: 600,
              color: "var(--accent)",
              background: "var(--bg-elev)",
              padding: "3px 7px",
              borderRadius: 999,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {predictionPill}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      // Cycle 165: frosted glass over the aurora — fill only; the mini
      // value, trend arrow, age, ProviderPill, and avatar stay opaque.
      className={frameStyles.glassChrome}
      // Cycle 383: same inert dev-gated voice trigger as the large-title
      // form above, so the long-press works on every mobile screen.
      data-voice-trigger="mobile-header"
      style={{
        // Cycle 57.2: grew the mobile header 52 → 60 to host the new
        // tight-cropped Logo without crowding the member-name text.
        height: 60,
        padding: "0 12px",
        borderBottom: "0.5px solid var(--glass-border)",
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexShrink: 0,
      }}
    >
      {/* Cycle 341: 40 → 48 (design audit — reads clearly at a glance
          without crowding the mini-reading cluster; 60px header height
          and padding/gap unchanged). */}
      <MemberAvatar
        avatarId={memberAvatarId}
        name={memberName ?? user?.display_name ?? "Sugar Alarm"}
        size={48}
      />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          lineHeight: 1.15,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--ink)",
            letterSpacing: "-0.005em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {memberName ?? user?.display_name ?? "Sugar Alarm"}
        </span>
      </div>
      {predictionPill && (
        <div
          style={{
            fontSize: "var(--type-floor)",
            fontWeight: 600,
            color: "var(--accent)",
            background: "var(--accent-soft)",
            padding: "3px 7px",
            borderRadius: 999,
            whiteSpace: "nowrap",
          }}
        >
          {predictionPill}
        </div>
      )}
      {/* Cycle 33: stale OR no-reading collapses the header readout to
          "— No Data" so the phone's status row matches the Now
          screen's hero copy. The age strip drops in that state. */}
      {current == null ? (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            flexShrink: 0,
          }}
        >
          <span
            className="tnum"
            style={{
              fontFamily: "var(--hero-font)",
              fontWeight: 600,
              fontSize: 17,
              color: "var(--zone-urgent)",
              letterSpacing: "-0.02em",
            }}
          >
            —
          </span>
          <span
            style={{
              fontSize: "var(--type-floor)",
              fontWeight: 600,
              color: "var(--zone-urgent)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            No Data
          </span>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            // Cycle 109: center (not baseline) so the trend arrow sits on
            // the number's midline, matching the kiosk header.
            alignItems: "center",
            gap: 4,
            flexShrink: 0,
          }}
        >
          <span
            className="tnum"
            style={{
              fontFamily: "var(--hero-font)",
              fontWeight: 600,
              fontSize: 17,
              color: heroColor,
              letterSpacing: "-0.02em",
            }}
          >
            {displayValue(current.sgv, units)}
          </span>
          {/* Cycle 109: trend arrow right of the mini value (same
              TrendGlyphSvg + trendInfo mapping the kiosk header uses),
              zone-colored, blank when no computable trend. Sized down
              for the smaller mobile value. */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: heroColor,
            }}
            aria-hidden="true"
          >
            <TrendGlyphSvg
              glyph={trendInfo(current.trend, current.trendRate).glyph}
              size={18}
            />
          </span>
          <span
            className="tnum"
            style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)", marginLeft: 2 }}
          >
            {fmtAge(ageMin)}
          </span>
        </div>
      )}
      {/* Cycle 109: provider pill (dot + name) replaces the bare dot —
          the same <ProviderPill> the kiosk header uses, sourced from the
          active member's reading provider (null → "Sugar Alarm", mirroring
          the kiosk's no-CGM rendering). */}
      <ProviderPill
        provider={
          cgmProvider !== undefined ? cgmProvider : current?.provider ?? null
        }
        stale={stale}
      />
      {/* Cycle 57.5: clock removed from mobile headers — phones already
          render the OS-level time in their own status bar, and the
          duplicate clock crowded the compact mobile header. The
          `fmtTime` / `now` / `h24` props are still accepted so the
          surrounding age + member text stays h24-correct; the bottom
          clock span is just gone. */}
    </div>
  );
}
